import { computed, ref, Ref, watchEffect, ComputedRef, nextTick } from 'vue';
import { ApplicationSection } from '@/shared/application/ApplicationSection';
import { createPageView } from '@/shared/infrastructure/composition/TrackConversions';
import { useRoute } from '@hfc/ui-core';

export function useSectionNavigation<T extends ApplicationSection> (originalSectionList: Array<T>, sectionSubmit:Ref, showLastStepInFlow?: boolean, allowGoBackWithoutValidation = false, onReachedLastStep?: () => void): {
  showProgressBar: ComputedRef<boolean>;
  filteredSectionList: ComputedRef<Array<T>>;
  currentSection: Ref<number>;
  nextSection: () => Promise<void>;
  resetSection: () => Promise<void>;
  prevSection: () => void;
  goToSection: (section: number, checkIsLast?: boolean) => void;
  stepToLastIncompleteSection: (section?: number) => Promise<void>;
} {
  const route = useRoute();
  // TODO review this logic, we probably need to contemplate the case in which we hide a step that is before the one showing.
  // We should change current step to 1 less but this change has a lot of impact and need to be tested.
  const filteredSectionList = computed(() => originalSectionList.filter((section) => section.conditionalShow ?
    section.conditionalShow() :
    true)
  );

  const sectionListLength: ComputedRef<number> = computed(() => filteredSectionList.value.length);
  const currentSection: Ref<number> = ref(0);
  const maxSection: Ref<number> = ref(0);
  const stepRef: Ref<null | {sectionSubmit: () => Promise<boolean | undefined>, sectionIsValid: () => Promise<boolean>}> = ref(null);

  const showProgressBar = computed(() => currentSection.value !== sectionListLength.value - (showLastStepInFlow ? 0 : 1));

  async function stepToLastIncompleteSection (section?: number): Promise<void> {
    const isValid = await stepRef.value?.sectionIsValid();
    const maxSectionIndex = filteredSectionList.value.length - 1;
    const targetSection = section !== undefined ? section : maxSectionIndex;

    if (!isValid || currentSection.value >= targetSection || currentSection.value >= maxSectionIndex) {
      return;
    }

    const prevSection = currentSection.value;
    await goToSection(currentSection.value + 1, true);
    await nextTick();

    if (currentSection.value === prevSection) {
      return;
    }

    return stepToLastIncompleteSection(section);
  }

  async function nextSection (): Promise<void> {
    goToSection(currentSection.value + 1);
  }

  function prevSection (): void {
    goToSection(currentSection.value - 1, true, true);
  }

  function stepChange (): void {
    window.scrollTo(0, 0);
    sendPageView();
  }

  function sendPageView (): void {
    createPageView(`${route.path}#${filteredSectionList.value[currentSection.value].shortTitle}`);
  }

  function isLastStep (): boolean {
    return currentSection.value === sectionListLength.value - 1 || filteredSectionList.value[currentSection.value].nextHidden?.() === true;
  }

  async function goToSection (section: number, checkIsLast?: boolean, isBack?: boolean): Promise<void> {
    const isLastSection = checkIsLast && maxSection.value === currentSection.value;
    let canContinue: boolean;

    try {
      const sectionIsValid = isLastSection || (isBack && allowGoBackWithoutValidation) || await stepRef.value?.sectionSubmit();
      canContinue = !!sectionIsValid;
    } catch (_error) {
      canContinue = false;
    }
    if (canContinue) {
      currentSection.value = section;
      maxSection.value = Math.max(maxSection.value, section);
      stepChange();

      if (isLastStep()) {
        onReachedLastStep?.();
      }
    }
  }

  async function resetSection (): Promise<void> {
    currentSection.value = 0;
  }

  // Call stepChange to initialize everything
  stepChange();

  watchEffect(() => {
    stepRef.value = sectionSubmit.value;
  });

  return {
    showProgressBar,
    currentSection,
    nextSection,
    resetSection,
    filteredSectionList,
    prevSection,
    goToSection,
    stepToLastIncompleteSection
  };
}
